import React from "react"
import md5 from "md5"
import { get, ref, set } from "firebase/database"
import gql from "graphql-tag"
import {
  ActionCodeSettings,
  UserCredential,
  sendPasswordResetEmail,
  signInWithRedirect,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  User,
  getIdToken,
  onIdTokenChanged,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  MultiFactorError,
  MultiFactorInfo,
  PhoneInfoOptions,
  RecaptchaVerifier,
  multiFactor,
  AuthError,
} from "firebase/auth"
import { useDispatch } from "react-redux"
import { sdk9Auth, sdk9db } from "../firebase"
import { logError } from "../utils/errors"
import { GenericStringObj } from "../types"
import { track } from "../utils/analytics"
import { isValidEmail } from "../utils/misc"
import { maybeUpdateLastActive } from "../actions/userActions"
import apolloClientNext, { requestSessionCookie } from "../ApolloClientNext"
import { ImpersonateUserDocument } from "./AuthProvider.graphqlTypes"
import { setImpersonation } from "../utils/users"

export const recaptchaButtonIds = {
  emailPasswordSignIn: "sign-in-button",
  googleSignIn: "google-sign-in-button",
  facebookSignIn: "facebook-sign-in-button",
}

export interface MFAFactorResult {
  factor: MultiFactorInfo
  timestamp: number
  enterVerificationCode: (verificationCode: string) => Promise<UserCredential>
}

export interface MFARequestEnrollFactorResult {
  timestamp: number
  enterVerificationCode: (
    verificationCode: string,
    displayName: string,
  ) => Promise<void>
}

export interface SFAResult {
  type: "sfa"
  credential: UserCredential
}
export interface MFAResult {
  type: "mfa"
  factors: MultiFactorInfo[]
  chooseFactor: (factorUid: string) => MFAFactorResult
}

type ContextProps = {
  getProviders: (email: string) => Promise<string[]>
  checkUserExists: (email: string) => Promise<{
    status: string
    signInMethods?: string[]
  }>
  getCurrentUserToken: (forceRefresh: boolean) => Promise<string | null>
  createUser: (email: string, password?: string) => Promise<User | null>
  getImpersonationToken: (idToken: string) => Promise<string>
  emailAndPasswordSignIn: (
    email: string,
    password: string,
    buttonId: string,
  ) => Promise<SFAResult | MFAResult>
  resumeSigninWithRedirectResult: () => Promise<SFAResult | MFAResult>
  googleSignIn: () => Promise<void>
  facebookSignIn: () => Promise<void>
  sendResetPassword: (
    signInEmail: string,
    codeSettings?: ActionCodeSettings,
  ) => Promise<void>
  sendAccountEmailVerification: () => Promise<void>
  requestEnrollFactor: (
    user: User,
    factor: {
      type: "phone"
      phoneNumber: string
      buttonId: string
    },
  ) => Promise<MFARequestEnrollFactorResult>
  unenrollFactor: (user: User, factor: MultiFactorInfo) => Promise<void>
  getMFAFactors: (user: User) => MultiFactorInfo[]
}

type ProviderProps = {
  children: React.ReactNode
}

const providersMap: GenericStringObj = {
  "google.com": "google",
  password: "email",
  "facebook.com": "facebook",
}

// full list https://firebase.google.com/docs/auth/admin/errors
export const firebaseErrorCodeMap: GenericStringObj = {
  "auth/wrong-password": "Incorrect password",
  "auth/email-already-in-use": "Account already exists",
  "auth/email-already-exists": "Account already exists",
  "auth/user-not-found": "That email address doesn't match an existing account",
  "auth/missing-email": "Email required",
  "auth/invalid-password": "Password must be at least 6 characters",
  "auth/too-many-requests":
    "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.",
  "auth/requires-recent-login":
    "For security reasons, please sign out and back in to use this feature",
  "auth/second-factor-already-in-use":
    "You have already enrolled that phone number",
}

const FIREBASE_IGNORE_ERRORS = [
  "auth/invalid-email",
  "auth/user-disabled",
  "auth/requires-recent-login",
]

const FIREBASE_ACCOUNTS_EXISTS_ERRORS = [
  "auth/email-already-in-use",
  "auth/wrong-password",
]

gql`
  mutation ImpersonateUser($userId: ID!) {
    impersonateUser(userId: $userId) {
      token
    }
  }
`

function useAuthProvider() {
  const getProviders = async (email: string) => {
    const signInMethods = await fetchSignInMethodsForEmail(sdk9Auth, email)
    return signInMethods.map((method) => providersMap[method])
  }

  const checkUserExists = async (
    email: string,
  ): Promise<{
    status: string
    signInMethods?: string[]
  }> => {
    /**
     * Returns an object with a status and optional signInMethods.
     * Object is one of no|autoCreated|selfCreated
     */
    if (!isValidEmail(email)) {
      return {
        status: "no",
      }
    }
    return fetchSignInMethodsForEmail(sdk9Auth, email)
      .then((signInMethods) => {
        if (signInMethods.length === 0) {
          // No sign in methods, so it's a new account.
          return {
            status: "no",
          }
        }
        // They have an account already, so figure out if we signed up or they did.
        return get(ref(sdk9db, `autoCreatedAccounts/${md5(email)}`)).then(
          (snap) => {
            if (snap.val()) {
              // We created it.
              return {
                status: "autoCreated",
              }
            }
            return {
              status: "selfCreated",
              signInMethods: signInMethods.map(
                (method) => providersMap[method],
              ),
            }
          },
        )
      })
      .catch((e) => {
        if (
          !firebaseErrorCodeMap[e.code] &&
          !FIREBASE_IGNORE_ERRORS.includes(e.code)
        ) {
          logError(e)
        }
        return { status: "no" }
      })
  }

  const getCurrentUserToken = async (forceRefresh: boolean) =>
    sdk9Auth.currentUser
      ? getIdToken(sdk9Auth.currentUser, forceRefresh).then((id) => id)
      : null

  const getError = (e: AuthError, extras?: Record<string, any>) => {
    if (
      !firebaseErrorCodeMap[e.code] &&
      !FIREBASE_IGNORE_ERRORS.includes(e.code)
    ) {
      logError(e, extras)
    }
    return new Error(
      firebaseErrorCodeMap[e.code] || e.message || "Error signing in.",
    )
  }

  const createUser = async (email: string, password?: string) => {
    /**
     * This is used (as of now) in the registration flow. It has to check
     * if the person already has an account, and if so, it will attempt
     * to sign them in instead of creating a new account. And if it can't
     * sign them in, it will return saying the account already exists,
     * but they need to sign in or create a password.
     */

    // Create an account for them. Will error if account exists already
    const passwordToUse = password || Math.random().toString(36).slice(2, 12)
    return createUserWithEmailAndPassword(sdk9Auth, email, passwordToUse)
      .then(async (userCredential) => {
        track("user-signed-in", { email })

        if (!password) {
          // We're creating an account for them.
          await set(ref(sdk9db, `autoCreatedAccounts/${md5(email)}`), true)
        }
        return userCredential.user
      })
      .catch(async (e) => {
        if (FIREBASE_ACCOUNTS_EXISTS_ERRORS.includes(e.code) && password) {
          try {
            const userCredential = await signInWithEmailAndPassword(
              sdk9Auth,
              email,
              password,
            )
            track("user-signed-in", { email })
            return userCredential.user
          } catch (error: any) {
            throw getError(error, { email })
          }
        }
        throw getError(e, { email })
      })
  }

  const unenrollFactor = async (user: User, factor: MultiFactorInfo) => {
    await multiFactor(user).unenroll(factor)
  }

  const getMFAFactors = (user: User) => {
    return multiFactor(user).enrolledFactors
  }

  const requestEnrollFactor = async (
    user: User,
    factorMeta: { type: "phone"; phoneNumber: string; buttonId: string },
  ) => {
    const recaptchaVerifier = new RecaptchaVerifier(
      sdk9Auth,
      factorMeta.buttonId,
      {
        size: "invisible",
      },
    )
    const multiFactorSession = await multiFactor(user).getSession()

    switch (factorMeta.type) {
      case "phone": {
        const phoneInfoOptions = {
          phoneNumber: factorMeta.phoneNumber,
          session: multiFactorSession,
        }
        const phoneAuthProvider = new PhoneAuthProvider(sdk9Auth)

        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier,
        )

        const enterVerificationCode = async (
          code: string,
          displayName: string,
        ) => {
          const cred = PhoneAuthProvider.credential(verificationId, code)
          const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

          await multiFactor(user).enroll(multiFactorAssertion, displayName)
        }

        return { enterVerificationCode, timestamp: Date.now() }
      }
      default:
        throw new Error("Unknown factor type")
    }
  }

  const maybeHandleMFAErrorFlow = (e: AuthError, buttonId: string) => {
    if (e.code === "auth/multi-factor-auth-required") {
      const error = e as MultiFactorError
      const resolver = getMultiFactorResolver(sdk9Auth, error)

      const chooseFactor = (factorUid: string) => {
        const factor = resolver.hints.find((hint) => hint.uid === factorUid)

        if (!factor) {
          throw new Error("Factor not found")
        }

        switch (factor.factorId) {
          case PhoneMultiFactorGenerator.FACTOR_ID: {
            const phoneInfoOptions: PhoneInfoOptions = {
              multiFactorHint: factor,
              session: resolver.session,
            }
            const recaptchaVerifier = new RecaptchaVerifier(
              sdk9Auth,
              buttonId,
              {
                size: "invisible",
              },
            )
            const phoneAuthProvider = new PhoneAuthProvider(sdk9Auth)
            const verificationIdPromise = phoneAuthProvider.verifyPhoneNumber(
              phoneInfoOptions,
              recaptchaVerifier,
            )

            const enterVerificationCode = async (verificationCode: string) => {
              const verificationId = await verificationIdPromise
              const cred = PhoneAuthProvider.credential(
                verificationId,
                verificationCode,
              )
              const multiFactorAssertion =
                PhoneMultiFactorGenerator.assertion(cred)
              return resolver.resolveSignIn(multiFactorAssertion)
            }

            return { factor, enterVerificationCode, timestamp: Date.now() }
          }
          default:
            throw new Error(`Unknown factor ${factor.factorId}`)
        }
      }

      return { type: "mfa" as const, factors: resolver.hints, chooseFactor }
    }

    return undefined
  }

  const emailAndPasswordSignIn: ContextProps["emailAndPasswordSignIn"] = async (
    email: string,
    password: string,
    buttonId: string,
  ) => {
    return signInWithEmailAndPassword(sdk9Auth, email, password)
      .then((credential) => {
        return { type: "sfa" as const, credential }
      })
      .catch((e) => {
        const result = maybeHandleMFAErrorFlow(e, buttonId)

        if (result) {
          return result
        }

        throw getError(e, { email })
      })
  }

  const sendAccountEmailVerification = async () => {
    if (!sdk9Auth.currentUser) {
      throw new Error("User not signed in.")
    }
    await sendEmailVerification(sdk9Auth.currentUser)
  }

  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider()
    provider.addScope("https://www.googleapis.com/auth/plus.login")
    provider.setCustomParameters({ prompt: "select_account" })
    await signInWithRedirect(sdk9Auth, provider)
  }

  const resumeSigninWithRedirectResult = () => {
    return getRedirectResult(sdk9Auth)
      .then((credential) => {
        if (!credential) {
          throw new Error("No credential returned")
        }

        return { type: "sfa" as const, credential }
      })
      .catch((e) => {
        const result = maybeHandleMFAErrorFlow(
          e,
          recaptchaButtonIds.googleSignIn,
        )

        if (result) {
          return result
        }

        throw getError(e)
      })
  }

  const facebookSignIn = async () => {
    const provider = new FacebookAuthProvider()
    provider.addScope("public_profile")
    provider.addScope("email")

    await signInWithRedirect(sdk9Auth, provider)
  }

  const sendResetPassword = async (
    signInEmail: string,
    codeSettings?: ActionCodeSettings,
  ) => {
    // check user isn't using provider first?
    const signInMethods = await fetchSignInMethodsForEmail(
      sdk9Auth,
      signInEmail,
    )
    if (
      signInMethods.length > 0 &&
      !signInMethods.some((method) => method === "password")
    ) {
      // Using provider, should not reset pass through us.
      throw new Error(
        `Account created through provider. Please sign in through ${signInMethods[0]} or reset your password through the provider's site.`,
      )
    }

    return sendPasswordResetEmail(sdk9Auth, signInEmail, codeSettings).catch(
      (e) => {
        throw getError(e)
      },
    )
  }

  const getImpersonationToken = async (idToken: string): Promise<string> => {
    const result = await apolloClientNext.mutate({
      mutation: ImpersonateUserDocument,
      variables: {
        userId: idToken,
      },
    })

    if (!result.data?.impersonateUser.token) {
      throw new Error("Unable to impersonate user")
    }

    const { token } = result.data.impersonateUser
    return token
  }

  return {
    createUser,
    getCurrentUserToken,
    checkUserExists,
    emailAndPasswordSignIn,
    getImpersonationToken,
    googleSignIn,
    facebookSignIn,
    sendResetPassword,
    getProviders,
    sendAccountEmailVerification,
    requestEnrollFactor,
    unenrollFactor,
    getMFAFactors,
    resumeSigninWithRedirectResult,
  }
}

const AuthContext = React.createContext<ContextProps>(undefined!)

export function AuthProvider({ children }: ProviderProps) {
  const dispatch = useDispatch()

  React.useEffect(() => {
    const unsubscribe = onIdTokenChanged(sdk9Auth, (user) => {
      if (user) {
        requestSessionCookie()
        dispatch(maybeUpdateLastActive(user.uid))
      } else {
        setImpersonation(undefined)
      }
    })
    return () => {
      unsubscribe()
    }
  }, [dispatch])

  const authProvider = useAuthProvider()

  return (
    <AuthContext.Provider value={authProvider}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => React.useContext(AuthContext)
